import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Alert
} from '@mui/material';

// DEPRECATED - Kept as backup
// import ConfigurationForm from './ConfigurationForm';
import ResearchAgentWizard from './ResearchAgentWizard';
import ResearchPreviewModal from './ResearchPreviewModal';
import UsageAlertBanner from '../usage/UsageAlertBanner';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost } from '../../utils/apiUtils';
import { createLogger } from '../../utils/debugUtils';

const logger = createLogger('ResearchAgentNewProject');

/**
 * ResearchAgentNewProject Component
 * Dedicated page for creating new research jobs
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onSuccess - Callback when job is created successfully
 */
const ResearchAgentNewProject = React.forwardRef((props, ref) => {
  const { onSuccess } = props;
  
  useAuth(); // Keep auth context for future use
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [availableFilters, setAvailableFilters] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentStepTitle, setCurrentStepTitle] = useState('UPLOAD YOUR LIST');
  const [navigationInfo, setNavigationInfo] = useState({
    hasNext: true,
    hasBack: false,
    onNext: null,
    onBack: null,
    wizardState: {}
  });

  // Preview modal state
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Fetch available filters on component mount
  useEffect(() => {
    fetchFilters();
  }, []);

  // Fetch filters for the source dropdown
  const fetchFilters = async () => {
    setLoading(true);
    try {
      const filters = await apiGet('/api/filters?includeCounts=true');
      setAvailableFilters(filters);
      logger.info(`Loaded ${filters.length} filters for source selection`);
    } catch (error) {
      logger.error('Error loading filters:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle job creation
  const handleCreateJob = async (jobConfig) => {
    setSubmitting(true);
    try {
      logger.info('Creating research job with config:', {
        jobName: jobConfig.name,
        websiteCount: jobConfig.websiteCount,
        columnName: jobConfig.columnName
      });

      // Call API to create job with proper authentication
      const response = await apiPost('/api/research/jobs', jobConfig);

      // Parse JSON from Response object (apiPost returns raw Response)
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API request failed with status ${response.status}`);
      }

      const data = await response.json();
      logger.info('Research job API response:', data);

      if (data && data.jobId) {
        logger.info('Research job created successfully:', { jobId: data.jobId });

        // Call onSuccess callback to redirect to Activity History
        if (onSuccess) {
          onSuccess();
        }
      } else {
        logger.error('Job creation response missing jobId:', data);
        throw new Error('Job creation response missing jobId');
      }

    } catch (error) {
      logger.error('Error creating research job:', error);
      setError(error.message || 'Failed to create research job. Please try again.');
      throw error; // Re-throw so caller knows it failed
    } finally {
      setSubmitting(false);
    }
  };

  // Handle job creation from wizard (called by Begin Research button)
  const handleCreateResearchJob = async () => {
    const { wizardState } = navigationInfo;

    // Final validation
    if (!wizardState.fileId) {
      setError('No file uploaded. Please go back to Step 1.');
      return;
    }

    if (!wizardState.selectedColumn) {
      setError('No website column selected. Please go back to Step 2.');
      return;
    }

    if (!wizardState.researchInstructions || !wizardState.researchInstructions.trim()) {
      setError('Please provide research instructions.');
      return;
    }

    if (!wizardState.jobName || !wizardState.jobName.trim()) {
      setError('Please provide a job name. Please go back to Step 1.');
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      logger.info('Building job config from wizard state', {
        fileId: wizardState.fileId,
        column: wizardState.selectedColumn,
        rowCount: wizardState.rowCount
      });

      // Build job configuration - use Phase 3 fields from wizard state (populated by template or defaults)
      const jobConfig = {
        sourceType: 'file',
        uploadedFileId: wizardState.fileId,
        columnName: wizardState.selectedColumn,
        filePath: wizardState.filePath,
        name: wizardState.jobName,
        instructions: wizardState.researchInstructions,
        enableWebSearch: wizardState.enableWebSearch || false, // Default false if not set
        webSearchInstructions: wizardState.webSearchInstructions || null,
        enableQualification: wizardState.enableQualification !== false, // Default true
        enableCustomFields: wizardState.enableCustomFields || false,
        customFields: wizardState.customFields || [],
        websiteCount: wizardState.rowCount || 0
      };

      // Call job creation
      await handleCreateJob(jobConfig);

      // Success: handleCreateJob calls onSuccess which redirects

    } catch (error) {
      // Error already logged and set by handleCreateJob
      // Just keep user on current step to show error
      logger.error('Failed to create research job from wizard:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle step change from wizard
  const handleStepChange = (step, stepTitle) => {
    setCurrentStep(step);
    setCurrentStepTitle(stepTitle);
  };

  // Handle navigation button info from wizard
  const handleNavigationInfo = (info) => {
    setNavigationInfo(info);
  };

  // Handle preview request - called when user clicks "Begin Research" on step 2
  const handlePreviewRequest = async () => {
    const { wizardState } = navigationInfo;

    // Validate required fields
    if (!wizardState.fileId) {
      setError('No file uploaded. Please go back to Step 1.');
      return;
    }

    if (!wizardState.selectedColumn) {
      setError('No website column selected. Please go back to Step 2.');
      return;
    }

    if (!wizardState.researchInstructions || !wizardState.researchInstructions.trim()) {
      setError('Please provide research instructions.');
      return;
    }

    setError(null);
    setPreviewError(null);
    setPreviewData(null);
    setShowPreview(true);
    setPreviewLoading(true);

    try {
      logger.info('Requesting preview for research job', {
        fileId: wizardState.fileId,
        column: wizardState.selectedColumn
      });

      const response = await apiPost('/api/research/preview', {
        fileId: wizardState.fileId,
        columnName: wizardState.selectedColumn,
        instructions: wizardState.researchInstructions,
        filePath: wizardState.filePath,
        enableWebSearch: wizardState.enableWebSearch
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Preview request failed with status ${response.status}`);
      }

      const data = await response.json();
      logger.info('Preview response received', {
        resultsCount: data.results?.length,
        totalWebsites: data.totalWebsites
      });

      setPreviewData(data);
    } catch (error) {
      logger.error('Error generating preview:', error);
      setPreviewError(error.message || 'Failed to generate preview. Please try again.');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Handle preview modal close
  const handlePreviewClose = () => {
    setShowPreview(false);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewLoading(false);
  };

  // Handle confirm from preview modal - creates the actual job
  const handlePreviewConfirm = async () => {
    setShowPreview(false);
    await handleCreateResearchJob();
  };

  // Determine if Continue button should be enabled based on current step
  const canProceed = () => {
    const { wizardState } = navigationInfo;

    switch (currentStep) {
      case 0: // Upload step
        return !!(wizardState.fileId && wizardState.jobName?.trim());
      case 1: // Select column step
        return !!wizardState.selectedColumn;
      case 2: // Research config step (final step - creates job)
        // Comprehensive validation before job creation
        return !!(
          wizardState.researchInstructions?.trim() &&
          wizardState.fileId &&
          wizardState.selectedColumn &&
          wizardState.jobName?.trim() &&
          !submitting // Disable during submission
        );
      default:
        return false;
    }
  };

  return (
    <Box>
      {/* Usage Alert Banner for low credits */}
      <UsageAlertBanner autoRefresh={true} showOnlyWarnings={true} />

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Step Header - Outside content box */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="h6"
              component="h1"
              gutterBottom
              sx={{
                textTransform: 'uppercase',
                letterSpacing: '0.15em',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: '#FFFFFF'
              }}
            >
              STEP {currentStep + 1}: {currentStepTitle}
            </Typography>
          </Box>

          <ResearchAgentWizard
            onSuccess={onSuccess}
            onStepChange={handleStepChange}
            onGetNavigationButton={handleNavigationInfo}
          />
          {/* OLD: ConfigurationForm - Kept as backup
          <ConfigurationForm
            availableFilters={availableFilters}
            onCreateJob={handleCreateJob}
            isSubmitting={submitting}
            onSuccess={onSuccess}
          />
          */}

          {/* Navigation Buttons - Outside Paper - Match List Cleaner */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            {/* Previous Button - Left Side */}
            {navigationInfo.hasBack && (
              <Button
                variant="outlined"
                onClick={navigationInfo.onBack}
                sx={{
                  textTransform: 'none',
                  borderRadius: '4px',
                  fontWeight: 'medium',
                  px: 3
                }}
              >
                Previous
              </Button>
            )}

            {/* Spacer when no back button to keep Continue on the right */}
            {!navigationInfo.hasBack && <Box />}

            {/* Continue/Preview Button - Right Side */}
            {navigationInfo.hasNext && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                {/* Skip Preview Button - Only show on final step */}
                {currentStep === 2 && (
                  <Button
                    variant="outlined"
                    onClick={handleCreateResearchJob}
                    disabled={!canProceed() || submitting}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '4px',
                      fontWeight: 'medium',
                      px: 3
                    }}
                  >
                    {submitting ? 'Creating Job...' : 'Skip Preview & Begin Research'}
                  </Button>
                )}

                {/* Preview Button */}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (currentStep === 2) {
                      // Final step: Show preview modal first
                      handlePreviewRequest();
                    } else {
                      // Earlier steps: Continue to next step
                      navigationInfo.onNext();
                    }
                  }}
                  disabled={!canProceed() || submitting || previewLoading}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '4px',
                    fontWeight: 'medium',
                    px: 3
                  }}
                >
                  {submitting
                    ? 'Creating Job...'
                    : previewLoading
                      ? 'Loading Preview...'
                      : (currentStep === 2 ? 'Preview Results' : 'Continue')
                  }
                </Button>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Preview Modal */}
      <ResearchPreviewModal
        open={showPreview}
        onClose={handlePreviewClose}
        onConfirm={handlePreviewConfirm}
        previewData={previewData}
        loading={previewLoading}
        error={previewError}
        totalWebsites={navigationInfo.wizardState?.rowCount || 0}
      />
    </Box>
  );
});

export default ResearchAgentNewProject;
